import React, { useState, useEffect, useCallback } from "react";
import { getDatabase, ref, get, remove } from "firebase/database";
import { app } from "./firebase/config";
import "./AdminUserManagement.css";

const AdminApiManagement = () => {
  const [apiHistory, setApiHistory] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [requestsPerPage] = useState(10);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [stats, setStats] = useState({
    totalRequests: 0,
    successRequests: 0,
    errorRequests: 0,
    successRate: 0,
  });
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  // Toast notification function
  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(
      () => setToast({ show: false, message: "", type: "success" }),
      3000
    );
  };

  const fetchApiHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const db = getDatabase(app);
      const apiHistoryRef = ref(db, "api_history");
      const snapshot = await get(apiHistoryRef);

      if (snapshot.exists()) {
        const data = snapshot.val();
        setApiHistory(data);
        calculateStats(data);
        showToast("Dữ liệu đã được cập nhật!");
      } else {
        setApiHistory({});
        setStats({
          totalRequests: 0,
          successRequests: 0,
          errorRequests: 0,
          successRate: 0,
        });
      }
    } catch (err) {
      console.error("Error fetching API history:", err);
      setError("Có lỗi khi tải dữ liệu lịch sử API");
      showToast("Lỗi khi tải dữ liệu: " + err.message, "error");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch API history data
  useEffect(() => {
    fetchApiHistory();
  }, [fetchApiHistory]);

  // Calculate statistics
  const calculateStats = (data) => {
    let totalRequests = 0;
    let successRequests = 0;
    let errorRequests = 0;

    Object.values(data).forEach((dateData) => {
      Object.values(dateData).forEach((request) => {
        totalRequests++;
        if (request.status_code >= 200 && request.status_code < 300) {
          successRequests++;
        } else {
          errorRequests++;
        }
      });
    });

    const successRate =
      totalRequests > 0
        ? ((successRequests / totalRequests) * 100).toFixed(2)
        : 0;

    setStats({
      totalRequests,
      successRequests,
      errorRequests,
      successRate: parseFloat(successRate),
    });
  };

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleString("vi-VN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  // Get all API requests as flat array
  const getAllRequests = () => {
    const requests = [];
    Object.entries(apiHistory).forEach(([date, dateData]) => {
      Object.entries(dateData).forEach(([id, request]) => {
        requests.push({
          id,
          date,
          ...request,
        });
      });
    });
    return requests.sort(
      (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
    );
  };

  // Filter requests
  const getFilteredRequests = () => {
    let requests = getAllRequests();

    // Filter by date
    if (selectedDate) {
      requests = requests.filter((req) => req.date === selectedDate);
    }

    // Filter by search term
    if (searchTerm) {
      requests = requests.filter(
        (req) =>
          req.endpoint?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          req.user_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          req.request_data?.message
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== "all") {
      if (statusFilter === "success") {
        requests = requests.filter(
          (req) => req.status_code >= 200 && req.status_code < 300
        );
      } else if (statusFilter === "error") {
        requests = requests.filter(
          (req) => req.status_code < 200 || req.status_code >= 300
        );
      }
    }

    return requests;
  };

  // Delete API record
  const deleteApiRecord = async (date, id) => {
    if (!window.confirm("Bạn có chắc chắn muốn xóa bản ghi này?")) return;

    try {
      const db = getDatabase(app);
      const recordRef = ref(db, `api_history/${date}/${id}`);
      await remove(recordRef);

      // Refresh data
      await fetchApiHistory();
      showToast("Đã xóa bản ghi thành công!");
    } catch (err) {
      console.error("Error deleting API record:", err);
      showToast("Có lỗi khi xóa bản ghi: " + err.message, "error");
    }
  };

  // Get unique dates
  const getUniqueDates = () => {
    return Object.keys(apiHistory).sort().reverse();
  };

  // Get status badge class
  const getStatusBadgeClass = (statusCode) => {
    if (statusCode >= 200 && statusCode < 300) return "success";
    if (statusCode >= 400 && statusCode < 500) return "warning";
    if (statusCode >= 500) return "inactive";
    return "inactive";
  };

  // Get status text
  const getStatusText = (statusCode) => {
    if (statusCode >= 200 && statusCode < 300) return "Thành công";
    if (statusCode >= 400 && statusCode < 500) return "Lỗi client";
    if (statusCode >= 500) return "Lỗi server";
    return "Không xác định";
  };

  // Open request detail modal
  const openRequestDetailModal = (request) => {
    setSelectedRequest(request);
    setShowDetailModal(true);
  };

  // Close request detail modal
  const closeRequestDetailModal = () => {
    setShowDetailModal(false);
    setSelectedRequest(null);
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setSelectedDate("");
    setCurrentPage(1);
  };

  // Pagination
  const filteredRequests = getFilteredRequests();
  const indexOfLastRequest = currentPage * requestsPerPage;
  const indexOfFirstRequest = indexOfLastRequest - requestsPerPage;
  const currentRequests = filteredRequests.slice(
    indexOfFirstRequest,
    indexOfLastRequest
  );
  const totalPages = Math.ceil(filteredRequests.length / requestsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPagination = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (totalPages <= 1) return null;

    return (
      <div className="pagination-container">
        <div className="pagination-info">
          Hiển thị {indexOfFirstRequest + 1}-
          {Math.min(indexOfLastRequest, filteredRequests.length)} của{" "}
          {filteredRequests.length} API requests
        </div>
        <div className="pagination-buttons">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-button"
          >
            Trước
          </button>

          {startPage > 1 && (
            <>
              <button
                onClick={() => handlePageChange(1)}
                className="pagination-button"
              >
                1
              </button>
              {startPage > 2 && <span className="pagination-dots">...</span>}
            </>
          )}

          {pageNumbers.map((number) => (
            <button
              key={number}
              onClick={() => handlePageChange(number)}
              className={`pagination-button ${
                currentPage === number ? "active" : ""
              }`}
            >
              {number}
            </button>
          ))}

          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && (
                <span className="pagination-dots">...</span>
              )}
              <button
                onClick={() => handlePageChange(totalPages)}
                className="pagination-button"
              >
                {totalPages}
              </button>
            </>
          )}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-button"
          >
            Sau
          </button>
        </div>
      </div>
    );
  };

  // Skeleton loading component
  const SkeletonLoader = () => (
    <div className="skeleton-container">
      {[...Array(5)].map((_, index) => (
        <tr key={index} className="skeleton-row">
          <td className="skeleton skeleton-text"></td>
          <td className="skeleton skeleton-text"></td>
          <td className="skeleton skeleton-badge"></td>
          <td className="skeleton skeleton-text"></td>
          <td className="skeleton skeleton-badge"></td>
          <td className="skeleton skeleton-text"></td>
          <td className="skeleton skeleton-text"></td>
        </tr>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="admin-content">
        <div className="loading-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <div className="loading-text">
              <h3>Đang tải dữ liệu...</h3>
              <p>Vui lòng chờ trong giây lát</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-content">
        <div className="error-container">
          <div className="error-icon">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="error-title">Có lỗi xảy ra</h3>
          <p className="error-message">{error}</p>
          <button className="btn btn-primary" onClick={fetchApiHistory}>
            Thử lại
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-content">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast ${toast.type}`}>
          <div className="toast-content">
            <div className="toast-icon">
              {toast.type === "success" ? (
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>
            <div className="toast-message">{toast.message}</div>
          </div>
        </div>
      )}

      {/* Request Detail Modal */}
      {showDetailModal && selectedRequest && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <div className="modal-header">
              <div className="modal-title">
                <div className="user-avatar">
                  {selectedRequest.method
                    ? selectedRequest.method.charAt(0).toUpperCase()
                    : "A"}
                </div>
                <div>
                  <h3>Chi tiết API Request</h3>
                  <p>{selectedRequest.endpoint || "N/A"}</p>
                </div>
              </div>
              <div className="modal-close">
                <span
                  className={`status-badge ${getStatusBadgeClass(
                    selectedRequest.status_code
                  )}`}
                >
                  {selectedRequest.status_code} -{" "}
                  {getStatusText(selectedRequest.status_code)}
                </span>
                <button onClick={closeRequestDetailModal}>
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Thời gian</label>
                  <input
                    type="text"
                    value={formatDate(selectedRequest.timestamp)}
                    disabled
                    className="form-input disabled"
                  />
                </div>
                <div className="form-group">
                  <label>Endpoint</label>
                  <input
                    type="text"
                    value={selectedRequest.endpoint || "N/A"}
                    disabled
                    className="form-input disabled"
                  />
                </div>
                <div className="form-group">
                  <label>Method</label>
                  <input
                    type="text"
                    value={selectedRequest.method || "N/A"}
                    disabled
                    className="form-input disabled"
                  />
                </div>
                <div className="form-group">
                  <label>User ID</label>
                  <input
                    type="text"
                    value={selectedRequest.user_id || "N/A"}
                    disabled
                    className="form-input disabled"
                  />
                </div>
                <div className="form-group">
                  <label>Status Code</label>
                  <input
                    type="text"
                    value={`${selectedRequest.status_code} - ${getStatusText(
                      selectedRequest.status_code
                    )}`}
                    disabled
                    className="form-input disabled"
                  />
                </div>
                <div className="form-group">
                  <label>Response Time</label>
                  <input
                    type="text"
                    value={
                      selectedRequest.response_time
                        ? `${selectedRequest.response_time}ms`
                        : "N/A"
                    }
                    disabled
                    className="form-input disabled"
                  />
                </div>
              </div>

              <div className="fanpages-section">
                <h5>Request Data</h5>
                <div className="table-content">
                  <pre className="payment-json">
                    {JSON.stringify(
                      selectedRequest.request_data || {},
                      null,
                      2
                    )}
                  </pre>
                </div>
              </div>

              <div className="fanpages-section">
                <h5>Response Data</h5>
                <div className="table-content">
                  <pre className="payment-json">
                    {JSON.stringify(
                      selectedRequest.response_data || {},
                      null,
                      2
                    )}
                  </pre>
                </div>
              </div>

              <div className="fanpages-section">
                <h5>Thông tin đầy đủ</h5>
                <div className="table-content">
                  <pre className="payment-json">
                    {JSON.stringify(selectedRequest, null, 2)}
                  </pre>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <div className="user-id-display">ID: {selectedRequest.id}</div>
              <div className="modal-actions">
                <button
                  className="btn btn-secondary"
                  onClick={closeRequestDetailModal}
                >
                  Đóng
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => {
                    if (
                      window.confirm(
                        "Bạn có chắc chắn muốn xóa bản ghi API này?"
                      )
                    ) {
                      deleteApiRecord(selectedRequest.date, selectedRequest.id);
                      closeRequestDetailModal();
                    }
                  }}
                >
                  Xóa
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminApiManagement;
